import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { Stack, Button, Divider, Typography, Alert } from '@mui/material';
// config
import { HOST_API } from '../../config';
// hooks
import useLocales from '../../hooks/useLocales';

// ----------------------------------------------------------------------

const GoogleIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z"
    />
    <path
      fill="#FBBC05"
      d="M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a11.97 11.97 0 0 0 0 10.76l3.98-3.09z"
    />
    <path
      fill="#EA4335"
      d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.69 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"
    />
  </svg>
);

const AppleIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M16.365 1.43c0 1.14-.47 2.2-1.22 3.02-.8.9-2.09 1.6-3.17 1.52-.14-1.1.42-2.27 1.16-3.05.82-.88 2.22-1.53 3.23-1.49zM20.8 17.3c-.57 1.3-.85 1.88-1.58 3.03-1.02 1.6-2.46 3.6-4.25 3.61-1.59.02-2-1.04-4.16-1.03-2.16.01-2.61 1.06-4.2 1.04-1.79-.02-3.15-1.82-4.17-3.42C.53 17.05-.06 12.5 1.68 9.9c1.24-1.85 3.19-2.93 5.02-2.93 1.87 0 3.04 1.04 4.58 1.04 1.5 0 2.41-1.05 4.57-1.05 1.63 0 3.36.89 4.59 2.43-4.03 2.21-3.38 7.97.36 8.91z"
    />
  </svg>
);

// ----------------------------------------------------------------------

function readOAuthError(search) {
  const params = new URLSearchParams(search);
  const error = params.get('error');
  if (!error) {
    return null;
  }
  return params.get('error_description') || error;
}

export default function AuthSocial() {
  const { translate } = useLocales();
  const { search } = useLocation();
  const [pendingProvider, setPendingProvider] = useState(null);

  const oauthError = useMemo(() => readOAuthError(search), [search]);

  const handleSocialLogin = (provider) => {
    setPendingProvider(provider);
    const apiBase = HOST_API.replace(/\/+$/, '');
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/login`);
    const referralCode = new URLSearchParams(search).get('ref');
    window.location.assign(`${apiBase}/api/auth/${provider}?redirect_uri=${redirectUri}${referralCode ? `&ref=${encodeURIComponent(referralCode)}` : ''}`);
  };

  return (
    <>
      <Stack data-ph-capture="safe" spacing={1.5}>
        {oauthError && (
          <Alert className="ph-mask" severity="error">
            {translate('auth.social.error', { message: oauthError })}
          </Alert>
        )}

        <Button
          fullWidth
          size="large"
          color="inherit"
          variant="outlined"
          startIcon={GoogleIcon}
          disabled={Boolean(pendingProvider)}
          onClick={() => handleSocialLogin('google')}
          sx={{
            borderColor: (theme) => theme.palette.grey[500_32],
            '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' },
          }}
        >
          {pendingProvider === 'google'
            ? translate('auth.social.redirecting')
            : translate('auth.social.google')}
        </Button>

        <Button
          fullWidth
          size="large"
          color="inherit"
          variant="outlined"
          startIcon={AppleIcon}
          disabled={Boolean(pendingProvider)}
          onClick={() => handleSocialLogin('apple')}
          sx={{
            borderColor: (theme) => theme.palette.grey[500_32],
            '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' },
          }}
        >
          {pendingProvider === 'apple'
            ? translate('auth.social.redirecting')
            : translate('auth.social.apple')}
        </Button>
      </Stack>

      <Divider data-ph-capture="safe" sx={{ my: 3 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', px: 1 }}>
          {translate('auth.social.divider')}
        </Typography>
      </Divider>
    </>
  );
}
